import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Grid2, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { FormField, FormFieldType } from '@enroller/admissions-components';
import { usePageTitle } from '@enroller/utils';
import { awardDefaultValues, BiographyFormValues, biographySchema, defaultValues } from './BiographyFormValues';
import { yesNoOptions } from '../../../constants/yesNoOptions';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext';

const MAX_CHARACTERS_DESCRIPTION = 1000;

const feesRangeOptions = [
  {
    label: '$2,000 - $4,000',
    value: '$2,000 - $4,000',
  },
  {
    label: '$4,000 - $6,000',
    value: '$4,000 - $6,000',
  },
  {
    label: '$6,000 - $8,000',
    value: '$6,000 - $8,000',
  },
  {
    label: '$8,000 - $10,000',
    value: '$8,000 - $10,000',
  },
  {
    label: '$10,000+',
    value: '$10,000+',
  },
];

// TODO: this list needs to be updated to show latest Zoho values
const activitiesGroupedOptions = [
  {
    group: 'Sports',
    options: [
      { label: 'Swimming', value: 'Swimming' },
      { label: 'Volleyball', value: 'Volleyball' },
      { label: 'Running / Track', value: 'Running / Track' },
    ],
  },
  {
    group: 'The Arts',
    options: [
      { label: 'Calligraphy', value: 'Calligraphy' },
      { label: 'Photography', value: 'Photography' },
      { label: 'Singing', value: 'Singing' },
    ],
  },
  {
    group: 'Other Hobbies & Interests',
    options: [
      { label: 'Baking', value: 'Baking' },
      { label: 'Cooking', value: 'Cooking' },
      { label: 'Gardening', value: 'Gardening' },
      { label: 'Reading', value: 'Reading' },
      { label: 'Movies', value: 'Movies' },
      { label: 'Visiting Museums', value: 'Visiting Museums' },
    ],
  },
];

interface BiographyFormProps {
  applicationData?: BiographyFormValues;
  submitApplication: (data: FieldValues) => void;
}

export const BiographyForm = ({ applicationData }: BiographyFormProps) => {
  const { setSubmit, setHeaderTitle } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, triggerErrors, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Biography', subtitle: '' });
    setPageTitle('Biography');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? biographySchema.required : biographySchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const { control, handleSubmit, reset, watch, trigger } = useForm<BiographyFormValues>({
    defaultValues,
    resolver,
  });

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  const {
    fields: awards,
    append: addAward,
    remove: removeAward,
  } = useFieldArray({
    control,
    name: 'awards',
  });

  // TODO: remove the object access applicationData?.biography once this form is integrated to GraphQL
  useEffect(() => {
    if (applicationData) {
      reset({
        ...defaultValues,
        ...applicationData,
      });
    }
  }, [applicationData, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(
        (data) => {
          setApplicationPageData('biography', data);
          if (validationMode === ValidationType.Full) triggerErrors();
          console.log('Form submitted:', data);
        },
        () => triggerErrors(),
      )(),
    [handleSubmit, setApplicationPageData, triggerErrors, validationMode],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  const watchWillingToPayFees = watch('general.willingToPayFees.value');
  const activity1Watch = watch('activities.activity1.type');
  const activity2Watch = watch('activities.activity2.type');
  const activity3Watch = watch('activities.activity3.type');
  const activitiesDisabledOptions = [activity1Watch, activity2Watch, activity3Watch];

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">General</Typography>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Have you ever taken part in a program with another exchange?"
              name="general.participatePreviousExchange"
              control={control}
              options={yesNoOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Would you be willing to attend a private school we offer in the program and pay the additional fees charged by the school?"
              name="general.willingToPayFees.value"
              control={control}
              options={yesNoOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Fees for private schools vary greatly. Please indicate below the range you can spend:"
              name="general.willingToPayFees.range"
              control={control}
              disabled={watchWillingToPayFees !== 'yes'}
              options={feesRangeOptions}
            />
          </Grid2>
        </Grid2>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Favorite Activities</Typography>
          </Grid2>
          <Alert variant="outlined" severity="info">
            <strong>IMPORTANT:</strong> From the interests and activities selected above, you must describe the three (3) activities in which you spend most of
            your time outside of school. Explain what you do, why you enjoy it and how much time you spend on it. These should be 4-6 sentences long. You host
            family will pick you based on this information so be truthful and descriptive.
            <br />
            <br />
            <strong>Note:</strong> If you cannot find the activities below, please add them in the above section so they can be selected here.
          </Alert>
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Autocomplete}
                  label="Most Favorite Activity"
                  name="activities.activity1.type"
                  control={control}
                  groupedOptions={activitiesGroupedOptions}
                  disabledOptions={activitiesDisabledOptions}
                />
              </Grid2>
              <Grid2 size={12}>
                <FormField
                  type={FormFieldType.Text}
                  label="Describe why this is one of your favorite activities"
                  name="activities.activity1.description"
                  control={control}
                  multiline
                  rows={6}
                  disabled={!activity1Watch}
                  maxLength={MAX_CHARACTERS_DESCRIPTION}
                />
              </Grid2>
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Autocomplete}
                  label="Second Most Favorite Activity"
                  name="activities.activity2.type"
                  control={control}
                  groupedOptions={activitiesGroupedOptions}
                  disabledOptions={activitiesDisabledOptions}
                />
              </Grid2>
              <Grid2 size={12}>
                <FormField
                  type={FormFieldType.Text}
                  label="Describe why this is one of your favorite activities"
                  name="activities.activity2.description"
                  control={control}
                  multiline
                  rows={6}
                  disabled={!activity2Watch}
                  maxLength={MAX_CHARACTERS_DESCRIPTION}
                />
              </Grid2>
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Autocomplete}
                  label="Third Most Favorite Activity"
                  name="activities.activity3.type"
                  control={control}
                  groupedOptions={activitiesGroupedOptions}
                  disabledOptions={activitiesDisabledOptions}
                />
              </Grid2>
              <Grid2 size={12}>
                <FormField
                  type={FormFieldType.Text}
                  label="Describe why this is one of your favorite activities"
                  name="activities.activity3.description"
                  control={control}
                  multiline
                  rows={6}
                  disabled={!activity3Watch}
                  maxLength={MAX_CHARACTERS_DESCRIPTION}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Student Bio</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              Write 4-6 sentence biography about yourself. This biography will be posted on Greenheart&apos;s website. The biography must not contain any
              personally identifiable information such as the student&apos;s last name, parent&apos;s names, city of residence, school name, etc.
            </Alert>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Bio"
              name="bio.studentBio"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Student's Video Introduction Link" control={control} name="bio.videoIntroductionLink" />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Awards & Achievements</Typography>
          </Grid2>
          {awards.map((item, index) => (
            <Grid2 size={12} key={item.id}>
              <Paper sx={{ p: 2 }}>
                <Grid2 container spacing={3}>
                  <Grid2 size={3}>
                    <FormField type={FormFieldType.Text} label="Award / Achievement Name" control={control} name={`awards.${index}.name`} />
                  </Grid2>
                  <Grid2 size={3}>
                    <FormField type={FormFieldType.Text} label="Institution Name" control={control} name={`awards.${index}.institutionName`} />
                  </Grid2>
                  <Grid2 size={3}>
                    <FormField type={FormFieldType.Text} label="Reason For Receiving" control={control} name={`awards.${index}.reason`} />
                  </Grid2>
                  <Grid2 size={3}>
                    <FormField
                      type={FormFieldType.Date}
                      label="Month / Year Received"
                      control={control}
                      name={`awards.${index}.date`}
                      displayDays={false}
                      max={0.1}
                      maxPeriod="days"
                      maxDirection="after"
                    />
                  </Grid2>
                  <Grid2 size={12}>
                    <Button variant="outlined" color="error" onClick={() => removeAward(index)}>
                      Delete
                    </Button>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>
          ))}
          <Grid2 size={12}>
            <Button startIcon={<AddIcon />} color="primary" variant="outlined" onClick={() => addAward(awardDefaultValues)}>
              Add an award / achievement
            </Button>
          </Grid2>
        </Grid2>
      </Paper>
    </Stack>
  );
};
